/**
 * WebSocket Provider for real-time messaging
 *
 * Provides WebSocket connection context to the entire app.
 * Manages a single WebSocket connection that is shared across all components.
 *
 * Features:
 * - Auto-sends pending messages when connection is restored
 * - Manages offline message queue with localStorage
 */

import React, { createContext, useContext, type ReactNode, useEffect } from 'react'
import { useWebSocket, type UseWebSocketReturn, usePendingMessages } from '@/hooks/messaging'

export interface WebSocketContextValue extends UseWebSocketReturn {
  // Pending message management
  queueMessage: (conversationId: number, content: string) => string // Returns pending message ID
  getPendingForConversation: (conversationId: number) => any[]
  retryFailedMessages: () => void
  clearFailedMessages: () => void
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined)

interface WebSocketProviderProps {
  children: ReactNode
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const websocket = useWebSocket()
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pendingMessages,
    queueMessage: queuePendingMessage,
    updateMessageStatus,
    markAsSent,
    incrementRetryCount,
    getPendingForConversation,
    getMessagesToSend,
    clearFailedMessages,
    retryFailedMessages,
  } = usePendingMessages()

  // Auto-send pending messages when WebSocket connects
  useEffect(() => {
    if (!websocket.isConnected) return

    const messagesToSend = getMessagesToSend()

    if (messagesToSend.length === 0) return

    console.log(`WebSocket connected - sending ${messagesToSend.length} pending messages`)

    // Send each pending message
    messagesToSend.forEach(async (msg) => {
      try {
        updateMessageStatus(msg.id, 'sending')

        // Send via WebSocket
        websocket.sendMessage(msg.conversationId, msg.content)

        // Mark as sent after a short delay (WebSocket is fire-and-forget)
        // In production, you'd want confirmation from backend
        setTimeout(() => {
          markAsSent(msg.id)
          console.log(`Pending message ${msg.id} sent successfully`)
        }, 500)
      } catch (error) {
        console.error(`Failed to send pending message ${msg.id}:`, error)
        incrementRetryCount(msg.id)
      }
    })
  }, [
    websocket.isConnected,
    getMessagesToSend,
    websocket.sendMessage,
    updateMessageStatus,
    markAsSent,
    incrementRetryCount,
  ])

  // Wrapper function that queues message and returns ID
  const queueMessage = (conversationId: number, content: string): string => {
    const pendingMsg = queuePendingMessage(conversationId, content)
    return pendingMsg.id
  }

  const contextValue: WebSocketContextValue = {
    ...websocket,
    queueMessage,
    getPendingForConversation,
    retryFailedMessages,
    clearFailedMessages,
  }

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
}

/**
 * Hook to use WebSocket context
 *
 * @returns WebSocket context value with pending message management
 * @throws Error if used outside of WebSocketProvider
 */
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext)

  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider')
  }

  return context
}
